import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import { ComponentItem } from "designSystem/types/component-item-types";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { useLocale } from "designSystem/locale";
import AutoCompleteSelect from "designSystem/components/select/AutoCompleteSelect";
import Text from "designSystem/components/text/Text";
import { convertNamesResultToComponentItem } from "csi/middleware/common";
import Breadcrumb from "csi/page/Breadcrumb";
import SearchManager from "csi/page/SearchManager";
import reflectionHints from "csi/reflectionHints";
import sessionConstants from "csi/sessionConstants";
import { setLocationHash } from "csi/utility/routerUtility";
import { useDataSetupStore } from "csi/state/datasetup";
import { useStore } from "csi/state/hooks";
import { getLocationForUrl } from "csi/utility/routerUtility";
import { observer } from "mobx-react-lite";
import React, { SyntheticEvent, useState } from "react";

const PREFIX = "DataSetupInstance";

const classes = {
	status: `${PREFIX}status`,
};

const StyledFlexRowCenter = styled(FlexRowCenter)((
	{
		theme,
	},
) => ({
	[`& .${classes.status}`]: {
		backgroundColor: "var(--StateOfNodeOnBreadcrumbBackgroundColor)",
		height: 22,
		marginLeft: theme.spacing(1),
		
		"& .MuiChip-label": {
			color: theme.palette.background.paper,
		},
	},
}));

// use single object for state rather than individual states for each property to reduce number of renders;
// state changes are not batched together in all situations
class DataSetupInstanceState {
	constructor(
		public name: string = "",
		public searching = false,
		public loading = false,
		public options: ComponentItem[] = [],
	) { }
}

export const DataSetupInstance = observer(() => {
	const loc = useLocale();
	const dataSetupStore = useDataSetupStore();
	const { routerStore } = useStore();
	const { locationUrl, locationName, locationNode } = routerStore;
	const { enableBreadcrumbSearch } = sessionConstants;

	const [state, setState] = useState<DataSetupInstanceState>(new DataSetupInstanceState());
	const { loading, name, options, searching } = state;

	const nodeStatus = reflectionHints.getLocationNodeStatus(locationNode, true);
	let selectedName: string;

	const onBlur = () => {
		// if a new value was selected, then render that name on blur rather than previous name;
		// the component will render before the newly selected instance will take affect and render
		setState(new DataSetupInstanceState(selectedName));
	};

	const onChange = (event: SyntheticEvent<Element, Event>, value: ComponentItem) => {
		// onChange is called when search string is empty (for some reason), so do nothing in that case
		if (value) {
			selectedName = loc.getComponentItemName(value);
			setLocationHash(getLocationForUrl(value.id));
		}
	};

	const onClickName = async () => {
		// switch to Autocomplete component to search for other item instances
		const { activeItem } = dataSetupStore;
		const { locationNode, locationAncestorNodes } = routerStore;
		const filterBySubtype = !!activeItem.subtypeUrl;
		// Make sure parent is actually parent of locationNode, on a Document ancestors doesn't contain the parent but will be non empty
		const possibleParent = locationAncestorNodes[0];
		const parent = possibleParent && possibleParent.$URL == locationNode.__Parent__ ? possibleParent : null;
		const store = parent ?
			SearchManager.getSiblingStore(locationNode, parent, filterBySubtype) :
			SearchManager.getGlobalStore(locationNode, filterBySubtype);

		setState(new DataSetupInstanceState(locationName, true, true));

		try {
			const results = await store.asyncQuery();
			const options = convertNamesResultToComponentItem(results);
			setState(new DataSetupInstanceState(locationName, true, false, options));
		} catch (err) {
			// cancel search if error
			onBlur();
		}
	};

	// reset state once finished searched and location name has caught up to selected name
	if (!searching && (name === locationName)) {
		setState(new DataSetupInstanceState());
	}

	return (
		<StyledFlexRowCenter flexShrink={0}>
			<AutoCompleteSelect
				enableSearch={enableBreadcrumbSearch}
				labelText={name || locationName}
				loading={!!loading}
				onBlur={onBlur}
				onChange={onChange}
				onClickName={onClickName}
				options={options}
				searchValue={{ id: locationUrl, name: locationName }}
				searchWidth={184}
				searching={searching}
			/>
			{nodeStatus && (
				<Chip
					className={classes.status}
					label={<Text semiBold text={loc.resource(nodeStatus).toLocaleUpperCase()} />}
					size="small"
				/>
			)}
			<Breadcrumb renderRevisionAndActions style={{ flexGrow: 1 }} />
		</StyledFlexRowCenter>
	);
});

export default DataSetupInstance;
